import { format } from "date-fns";
import Enumerable from "linq";
import { MfaSourceTypeEnum } from "../../../../common/enums/MfaSourceTypeEnum";
import { RecipientTypeEnum } from "../../../../common/enums/RecipientTypeEnum";
import { phoneNumberWithoutCountryCodeRegex } from "../../../../common/regex/RegEx";
import { fullOuterJoin } from "../../../../common/utilities/LinqExtensions";
import { DATE_TIME_FORMAT_STRING } from "../../../../common/utilities/TimeframeUtilities";
import { CheckListItemType } from "../../../../components/check-list/CheckList";
import { customizationSettingsFieldIsDirty, mergeUICustomizationSettings, toUICustomizationSettingsField } from "../../../../components/ui-customization-settings/UICustomizationSettingsUtils";
import { UserRecipientFieldIsDirtyType, UserRecipientFieldType } from "../../../../components/user-recipient-list/UserRecipientFieldTypes";
import { IEditUserDTO, IEditUserRecipientDTO, UserAndRoleAdministrationRoles } from "../../../../services/user-role-administration-service/Models";
import { UICustomizationModeTypeEnum, UserAssociatedRoleFieldIsDirtyType, UserAssociatedRoleFieldType, UserFieldIsDirtyType, UserFieldType } from "./UserFieldTypes";

export const NewUserID = -1;

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Validation Functions

export function generalIsValid(
    user: UserFieldType
): boolean {

    return (user.firstName?.trim()?.length ?? 0) > 0 &&
        (user.lastName?.trim()?.length ?? 0) > 0 &&
        // Bug 52761: DOC - Add User Error
        !!user.phoneNumber?.match(phoneNumberWithoutCountryCodeRegex) &&
        //!!user.phoneNumber?.match(/^\(\d{3}\) ?\d{3}( |-)?\d{4}|^\d{3}( |-)?\d{3}( |-)?\d{4}/) &&
        !!user.login?.match(/^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/);
}

export function alarmNotificationsValid(
    // user: UserFieldType
): boolean {
    return true;
}

export function membershipIsValid(
    // user: UserFieldType
): boolean {
    return true;
}

export function securityIsValid(
    // user: UserFieldType
): boolean {
    return true;
}

export function pageVisibilityIsValid(
    user: UserFieldType
): boolean {
    return user.uiCustomizationMode === UICustomizationModeTypeEnum.None ||
        (user.uiCustomizationMode === UICustomizationModeTypeEnum.Preset && !!user.uiCustomizationPresetID) ||
        (user.uiCustomizationMode === UICustomizationModeTypeEnum.Custom && !!user.customizationSettings);
}

// #endregion Validation Functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Merge Functions

export function mergeUsers(
    userModel: IEditUserDTO,
    userField: UserFieldType
): IEditUserDTO {

    const customizationSettingsJSON = JSON.stringify(mergeUICustomizationSettings(userField.customizationSettings));

    const user: IEditUserDTO = {
        ...userModel,
        id: userField.id,
        login: userField.login,
        firstName: userField.firstName,
        lastName: userField.lastName,
        companyName: userField.companyName,
        jobTitle: userField.jobTitle,
        jobFunction: userField.jobFunction,
        phoneNumber: userField.phoneNumber,
        timeZoneIndex: userField.timeZoneIndex,
        isPasswordChangeRequired: userField.requirePasswordChange,
        isDisabled: userField.disabled,
        isMobileAccessEnabled: userField.mobileAccessEnabled,
        associatedRoleIds: mergeAssociatedRoles(userField.associatedRoles),
        recipients: mergeRecipients(userModel.recipients ?? [], userField.recipients),
        emailNewUserWelcomeMessage: userField.sendNewUserNotification,
        externalSignInName: userField.externalSignInName,
        uiCustomizationPresetID: userField.uiCustomizationMode === UICustomizationModeTypeEnum.Preset
            ? userField.uiCustomizationPresetID
            : undefined,
        uiCustomizationSettingsJSON: userField.uiCustomizationMode === UICustomizationModeTypeEnum.Custom
            ? customizationSettingsJSON
            : undefined
    };

    return user;
}

function mergeRecipients(
    recipientModels: Array<IEditUserRecipientDTO>,
    recipientFields: Array<UserRecipientFieldType>
): Array<IEditUserRecipientDTO> {

    return fullOuterJoin(
        recipientFields,
        recipientModels,
        o => o.id,
        i => i.id,
        (o, i) => {
            return {
                recipientField: o,
                recipientModel: i
            };
        })

        // Only recipients to be updated and new recipients. Discard recipients to be deleted.
        .where(o => (!!o.recipientModel && !!o.recipientField) ||
            (!o.recipientModel && !!o.recipientField))
        .select((o/*, index*/) => {

            if (!!o.recipientModel && !!o.recipientField) {

                // recipient to be updated
                const recipient: IEditUserRecipientDTO = {
                    ...o.recipientModel,
                    type: o.recipientField.type,
                    subType: o.recipientField.subType,
                    to: o.recipientField.to,
                    disabled: o.recipientField.disabled,
                    cloneRecipientId: o.recipientField.cloneRecipientID
                };

                return recipient;

            } else {

                // recipient to be added
                const recipient: IEditUserRecipientDTO = {
                    id: o.recipientField.id,
                    type: o.recipientField.type,
                    subType: o.recipientField.subType,
                    to: o.recipientField.to,
                    disabled: o.recipientField.disabled,
                    cloneRecipientId: o.recipientField.cloneRecipientID,
                };

                return recipient;
            }
        })
        .toArray();
}

function mergeAssociatedRoles(
    associatedRoleFields: Array<UserAssociatedRoleFieldType>
): Array<number> {
    return associatedRoleFields.map(r => r.id);
}

// #endregion Merge Functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Field conversion functions

export function toUserField(
    dto: IEditUserDTO,
    allowSSO: boolean
): UserFieldType {

    const customizationSettings = dto.uiCustomizationSettingsJSON
        ? JSON.parse(dto.uiCustomizationSettingsJSON)
        : undefined;

    return {
        id: dto.id,
        firstName: dto.firstName,
        lastName: dto.lastName,
        companyName: dto.companyName,
        jobTitle: dto.jobTitle,
        jobFunction: dto.jobFunction,
        phoneNumber: getPlainPhoneNumber(dto.phoneNumber ?? "", RecipientTypeEnum.Text).phoneNumber,
        timeZoneIndex: dto.timeZoneIndex,
        login: dto.login,
        lastLoginTimestamp: formatTimestamp(dto.lastLoginTimestamp),
        sendNewUserNotification: dto.emailNewUserWelcomeMessage,
        requirePasswordChange: dto.isPasswordChangeRequired,
        disabled: dto.isDisabled,
        mobileAccessEnabled: dto.isMobileAccessEnabled,
        multipleCustomers: dto.multipleCustomers ?? false,
        isElynxAdmin: dto.isElynxAdmin ?? false,
        recipients: toUserRecipientFields(dto),
        associatedRoles: toAssociatedRoleFields(dto),
        createEmailRecipient: dto.id === NewUserID,
        createSmsRecipient: dto.id === NewUserID,
        createVoiceRecipient: dto.id === NewUserID,
        mfaEnabled: dto.mfaEnabled,
        mfaSource: dto.mfaSource !== undefined ? MfaSourceTypeEnum[dto.mfaSource] : undefined,
        externalSignInName: dto.externalSignInName,
        allowSSO: allowSSO,
        customizationSettings: toUICustomizationSettingsField(customizationSettings),
        hasUICustomizationSettings: !!dto.uiCustomizationSettingsJSON,
        uiCustomizationMode: dto.uiCustomizationSettingsJSON
            ? UICustomizationModeTypeEnum.Custom
            : dto.uiCustomizationPresetID
                ? UICustomizationModeTypeEnum.Preset
                : UICustomizationModeTypeEnum.None,
        uiCustomizationPresetID: dto.uiCustomizationPresetID
    };
}

function toUserRecipientFields(
    dto: IEditUserDTO
): Array<UserRecipientFieldType> {

    return dto.recipients?.map(r => {
        return {
            id: r.id,
            type: r.type,
            subType: r.subType,
            to: r.to,
            disabled: r.disabled,
            disabledTimestamp: r.disabledTimestamp,
            usageCount: r.usageCount,
            cloneRecipientID: undefined
        };
    }) ?? [];
}

function toAssociatedRoleFields(
    dto: IEditUserDTO
): Array<UserAssociatedRoleFieldType> {
    return dto.associatedRoleIds?.map(id => {
        return {
            id: id
        };
    }) ?? [];
}

// #endregion Field conversion functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Membership Step Functions

export function createAvailableRoleCheckListItems(
    availableRoles: UserAndRoleAdministrationRoles | undefined,
    selectedRoleFields: Array<UserAssociatedRoleFieldType>
): Array<CheckListItemType> {

    return Enumerable
        .from(availableRoles?.roles ?? [])
        .select(role => {

            const columnIndex = selectedRoleFields
                .findIndex(r => r.id === role.id);

            return {
                key: `${role.id}`,
                displayText: role.name,
                secondaryText: role.description,
                checked: columnIndex > -1,
                active: true,
            } as CheckListItemType;
        })
        .orderBy(c => c.displayText.toUpperCase())
        .toArray();
}

// #endregion Membership Step Functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region IsDirty Functions

export function userFieldIsDirty(user: UserFieldType, dirtyFields?: UserFieldIsDirtyType): boolean {
    return (
        dirtyFields?.id ||
        dirtyFields?.firstName ||
        dirtyFields?.lastName ||
        dirtyFields?.externalSignInName ||
        dirtyFields?.companyName ||
        dirtyFields?.jobTitle ||
        dirtyFields?.jobFunction ||
        dirtyFields?.phoneNumber ||
        dirtyFields?.timeZoneIndex ||
        dirtyFields?.login ||
        dirtyFields?.sendNewUserNotification ||
        dirtyFields?.requirePasswordChange ||
        dirtyFields?.disabled ||
        dirtyFields?.mobileAccessEnabled ||
        dirtyFields?.recipients?.some(r => userRecipientFieldIsDirty(r)) ||
        dirtyFields?.associatedRoles?.some(r => associatedRoleFieldIsDirty(r)) ||
        dirtyFields?.uiCustomizationMode ||
        (
            user.uiCustomizationMode === UICustomizationModeTypeEnum.Preset
                ? dirtyFields?.uiCustomizationPresetID
                : user.uiCustomizationMode === UICustomizationModeTypeEnum.Custom
                    ? customizationSettingsFieldIsDirty(dirtyFields?.customizationSettings)
                    : false
        )
    ) ?? false;
}

function userRecipientFieldIsDirty(dirtyFields?: UserRecipientFieldIsDirtyType): boolean {
    return (
        dirtyFields?.id ||
        dirtyFields?.type ||
        dirtyFields?.subType ||
        dirtyFields?.to ||
        dirtyFields?.disabled ||
        dirtyFields?.cloneRecipientID
    ) ?? false;
}

function associatedRoleFieldIsDirty(dirtyFields?: UserAssociatedRoleFieldIsDirtyType): boolean {
    return (
        dirtyFields?.id
    ) ?? false;
}

// #endregion IsDirty Functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Miscellaneous Functions

function formatTimestamp(timestamp: string | undefined): string {
    if (timestamp) {
        const date = new Date(timestamp);
        return format(date, DATE_TIME_FORMAT_STRING);
    }
    return "";
}

function getPlainPhoneNumber(fullNumber: string, recipientType: number): { phoneNumber: string, countryCode: string; } {

    let countryCode: string;
    let phoneNumber: string = fullNumber;

    if (phoneNumber.charAt(0) === "+") {
        const split = phoneNumber.split(' ');

        // Only text types can have international phone numbers
        countryCode = (recipientType === RecipientTypeEnum.Text)
            ? split[0].replace(/\D/g, "")
            : "1";


        if (split.length === 2) {
            phoneNumber = split[1].replace(/\D/g, "");
        } else {
            phoneNumber = phoneNumber.substring(2);
            countryCode = "1";
        }
    } else if (phoneNumber.charAt(0) === "1") {
        countryCode = "1";
        phoneNumber = phoneNumber.replace(/\D/g, "").substring(1);
    } else {
        countryCode = "1";
        phoneNumber = phoneNumber.replace(/\D/g, "");
    }

    phoneNumber = (phoneNumber.length > 10)
        ? phoneNumber.substring(0, 10)
        : phoneNumber;

    return { phoneNumber: phoneNumber, countryCode: countryCode };
}

// #endregion Miscellaneous Functions